import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";

const ease = [0.16, 1, 0.3, 1];

/**
 * PageShell — the standard V3 layout: reserves space for the fixed
 * left sidebar on desktop, adds top padding for the mobile top bar,
 * and applies a consistent editorial container.
 */
export function PageShell({ testId, children, backdrop = null }) {
    return (
        <main
            id="main"
            tabIndex={-1}
            data-testid={testId}
            className="focus:outline-none pt-16 lg:pt-0 min-h-screen relative"
            style={{ paddingLeft: "0", marginLeft: 0 }}
        >
            {backdrop}
            <div className="lg:pl-[var(--sidebar-w)] relative z-10">
                <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-12 md:py-20">
                    {children}
                </div>
            </div>
        </main>
    );
}

/**
 * PageHeader — Overline + serif title + optional kicker. Consistent
 * across every V3 route so pages read as chapters in the same book.
 */
export function PageHeader({ overline, title, kicker, id = "page-heading" }) {
    return (
        <header className="pt-4 md:pt-8 pb-10 md:pb-16 max-w-[900px]">
            {overline && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease }}
                    className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--navy)] mb-6 flex items-center gap-4"
                >
                    <span aria-hidden className="gold-thin" />
                    {overline}
                </motion.div>
            )}
            {title && (
                <motion.h1
                    id={id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease, delay: 0.05 }}
                    className="font-display font-light tracking-[-0.015em] leading-[1.02] text-[color:var(--navy)]"
                    style={{ fontSize: "clamp(40px, 6.2vw, 88px)" }}
                >
                    {title}
                </motion.h1>
            )}
            {kicker && (
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease, delay: 0.15 }}
                    className="mt-8 max-w-[62ch] text-[17px] md:text-[19px] leading-[1.65] text-[color:var(--navy)]/85"
                >
                    {kicker}
                </motion.p>
            )}
        </header>
    );
}

/** Elegant empty-state block used across Ideas / Research / Projects. */
export function EmptyState({ label }) {
    const { lang } = useLanguageSafe();
    const status = lang === "es" ? "En preparación"
        : lang === "nl" ? "In voorbereiding"
        : lang === "fr" ? "En préparation"
        : "In preparation";
    return (
        <div className="mt-6 border-t border-b border-[color:var(--line-strong)] py-16 md:py-20 grid grid-cols-12 gap-6" data-testid="empty-state">
            <div className="col-span-12 md:col-span-3 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                {status}
            </div>
            <div className="col-span-12 md:col-span-9">
                <p className="font-display italic text-[22px] md:text-[30px] leading-[1.25] text-[color:var(--navy)]/85 max-w-[26ch]">
                    {label}
                </p>
            </div>
        </div>
    );
}

// Guard against usage outside provider during SSR-like edge (won't happen here)
function useLanguageSafe() {
    try { return useLanguage(); } catch (_e) { return { lang: "en" }; }
}
